import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  consultasService as consultaService, 
  pacientesService as pacienteService,
  type Paciente 
} from '../services/api';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg } from '@fullcalendar/core';

// Tipo para os eventos do calendário
interface EventoCalendario {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  extendedProps: {
    pacienteId: string;
    valor: number;
    realizada: boolean;
    pagamentoRecebido: boolean;
  };
}

export default function Agenda() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState<EventoCalendario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);
  const [_consultas, _setConsultas] = useState<any[]>([]); // Prefixed with underscore to indicate intentionally unused
  const [_pacientes, _setPacientes] = useState<Paciente[]>([]); // Prefixed with underscore to indicate intentionally unused
  const [calendarView] = useState<string>(() => window.innerWidth < 576 ? 'timeGridWeek' : 'dayGridMonth');
  
  useEffect(() => {
    carregarConsultas();
  }, []);

  async function carregarConsultas() {
    try {
      setCarregando(true);
      setErroCarregamento(null);
      
      // Buscar consultas e pacientes
      const consultas = await consultaService.listarTodas();
      const pacientes = await pacienteService.listarTodos();
      
      // Garantir que consultas e pacientes são arrays
      const consultasArray = Array.isArray(consultas) ? consultas : [];
      const pacientesArray = Array.isArray(pacientes) ? pacientes : [];
      
      // Criar mapa de pacientes para acesso rápido
      const mapaPacientes: Record<string, Paciente> = pacientesArray.reduce((mapa, p) => {
        mapa[p.id] = p;
        return mapa;
      }, {} as Record<string, Paciente>);
      
      // Transformar consultas em eventos para o calendário com tratamento de IDs duplicados
      const eventosCalendario = consultasArray.map((consulta) => {
        const paciente = mapaPacientes[consulta.pacienteId];
        const nomePaciente = paciente ? paciente.nome : 'Paciente não encontrado';
        
        // Determinar cor com base no status
        let backgroundColor;
        if (!consulta.realizada) {
          backgroundColor = '#4285F4'; // Azul para consultas pendentes
        } else if (!consulta.pagamentoRecebido) {
          backgroundColor = '#FBBC05'; // Amarelo para realizadas sem pagamento
        } else {
          backgroundColor = '#34A853'; // Verde para concluídas
        }

        const dataEvento = `${consulta.data}T${consulta.hora}`;
        
        // Garantir ID único mesmo quando há duplicação nos dados
        // Usar combinação de ID + data para garantir unicidade
        const idUnico = `${consulta.id}-${consulta.data}`;
        
        return {
          id: idUnico, // ID único para cada evento do calendário
          consultaId: consulta.id, // Manter o ID original da consulta para navegação
          title: `${nomePaciente} - ${consulta.pagamentoRecebido ? '💰' : ''}`,
          start: dataEvento,
          end: adicionarDuracao(consulta.data, consulta.hora),
          backgroundColor,
          extendedProps: {
            pacienteId: consulta.pacienteId,
            valor: consulta.valor,
            realizada: consulta.realizada ?? false,
            pagamentoRecebido: consulta.pagamentoRecebido ?? false
          }
        };
      });
      
      // console.log(`Total de eventos: ${eventosCalendario.length}`);
      // Debug - verificar eventos de junho
      const eventosJunho = eventosCalendario.filter(e => e.start.includes('2025-06'));
      // console.log('Eventos de junho:', eventosJunho);
      
      setEventos(eventosCalendario);
    } catch (error) {
      console.error("Erro ao carregar consultas para o calendário:", error);
      setErroCarregamento("Não foi possível carregar as consultas. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }
  
  // Função para adicionar duração padrão de 1h à consulta
  function adicionarDuracao(data: string, hora: string): string {
    const dataHora = new Date(`${data}T${hora}`);
    dataHora.setHours(dataHora.getHours() + 1);
    
    // Garantir que retornamos uma string no formato ISO, adequada para eventos do calendário
    return dataHora.toISOString();
  }
  
  // Manipulador para clique em evento
  const handleEventClick = (clickInfo: EventClickArg) => {
    const evento = clickInfo.event;
    // Usar o ID da consulta original, não o ID do evento
    const consultaId = evento.extendedProps.consultaId || evento.id;
    navigate(`/consultas/editar/${consultaId}`);
  };
  
  // Manipulador para clique em data (criar nova consulta)
  const handleDateClick = (arg: any) => {
    const data = arg.dateStr.split('T')[0]; // formato YYYY-MM-DD
    const hora = arg.dateStr.includes('T') 
      ? arg.dateStr.split('T')[1].substring(0, 5) // HH:MM se tiver hora
      : '09:00'; // Horário padrão se clicar só no dia
    
    navigate(`/consultas/nova?data=${data}&hora=${hora}`);
  };
  
  // Função para atualizar dados quando a visualização muda - SEM recarregar consultas
  const handleViewChange = () => {
    // Implementation without using the 'info' parameter
  };
  
  // Função para quando as datas visíveis mudam (ex: mudar de mês)
  const handleDatesSet = () => {
    // Apenas registrar o intervalo de datas, sem recarregar
  };
  
  return (
    <div className="agenda-container">
      <h2 className="mb-4">Agenda de Consultas</h2>
      
      {erroCarregamento && (
        <div className="alert alert-danger mb-4">
          {erroCarregamento}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={carregarConsultas}>
            Tentar novamente
          </button>
        </div>
      )}
      
      {carregando ? (
        <div className="text-center my-5">
          <div className="spinner-border"></div>
          <p>Carregando agenda...</p>
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={calendarView}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              slotMinTime="07:00:00"
              slotMaxTime="22:00:00"
              locale="pt-br"
              events={eventos}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              height="auto"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5],
                startTime: '08:00',
                endTime: '18:00',
              }}
              buttonText={{
                today: 'Hoje',
                month: 'Mês',
                week: 'Semana',
                day: 'Dia',
                list: 'Lista'
              }}
              allDaySlot={false}
              nowIndicator={true}
              viewDidMount={handleViewChange}
              datesSet={handleDatesSet}
              showNonCurrentDates={true}
              fixedWeekCount={false}
              weekNumbers={false}
              firstDay={0}
            />
          </div>
        </div>
      )}
      
      <div className="mt-3 d-flex justify-content-between">
        <button 
          className="btn btn-outline-secondary" 
          onClick={() => navigate('/consultas')}
        >
          Voltar para Lista
        </button>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/consultas/nova')}
        >
          Nova Consulta
        </button>
      </div>
      
      <div className="mt-4">
        <h5>Legenda</h5>
        <div className="d-flex gap-4">
          <div className="d-flex align-items-center">
            <div className="me-2" style={{width: '15px', height: '15px', backgroundColor: '#4285F4', borderRadius: '50%'}}></div>
            <span>Pendente</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="me-2" style={{width: '15px', height: '15px', backgroundColor: '#FBBC05', borderRadius: '50%'}}></div>
            <span>Realizada (Aguardando pagamento)</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="me-2" style={{width: '15px', height: '15px', backgroundColor: '#34A853', borderRadius: '50%'}}></div>
            <span>Finalizada</span>
          </div>
        </div>
      </div>
    </div>
  );
}
